import { Injectable, inject } from "@angular/core";
import {
  UpdateAgendaDto,
  UserWeeklyScheduleExceptionsUpdateBlockDto,
  UserWeeklyScheduleUpdateBlockDto,
  CreateUserWeeklyScheduleExceptionDto,
  ResponseUserWeeklyScheduleDto,
  ResponseUserWeeklyScheduleExceptionDto,
  CreateUserWeeklyScheduleDto,
  UpdateUserWeeklyScheduleDto,
} from "../../api/models";
import { LoggedUserService } from "@keus-template/auth";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class AgendaMapperService {
  private readonly _loggedUserService = inject(LoggedUserService);

  /**
   * Mappa i dati correnti confrontandoli con lo stato iniziale per creare il DTO di aggiornamento
   */
  public mapUpdateAgendaDto(
    initialState: {
      schedule: ResponseUserWeeklyScheduleDto | null;
      exceptions: ResponseUserWeeklyScheduleExceptionDto[] | null;
    },
    currentState: {
      schedule: ResponseUserWeeklyScheduleDto | null;
      exceptions: ResponseUserWeeklyScheduleExceptionDto[] | null;
    },
  ): UpdateAgendaDto {
    const updateDto: UpdateAgendaDto = {};

    // Mappa gli aggiornamenti del schedule
    const scheduleUpdate = this._mapScheduleUpdate(
      initialState.schedule,
      currentState.schedule,
    );
    if (scheduleUpdate) {
      updateDto.schedule = scheduleUpdate;
    }

    // Mappa gli aggiornamenti delle exceptions
    const exceptionsUpdate = this._mapExceptionsUpdate(
      initialState.exceptions,
      currentState.exceptions,
    );
    if (exceptionsUpdate) {
      updateDto.exceptions = exceptionsUpdate;
    }

    return updateDto;
  }

  /**
   * Mappa gli aggiornamenti del weekly schedule
   */
  private _mapScheduleUpdate(
    initial: ResponseUserWeeklyScheduleDto | null,
    current: ResponseUserWeeklyScheduleDto | null,
  ): UserWeeklyScheduleUpdateBlockDto | null {
    // Caso 1: Non c'era schedule iniziale e ora c'è -> CREATE
    if (!initial && current) {
      return {
        create: this._mapToCreateScheduleDto(current),
      };
    }

    // Caso 2: C'era schedule iniziale e ora non c'è -> DELETE
    if (initial && !current) {
      return {
        delete: true,
      };
    }

    // Caso 3: Entrambi esistono, controlla se sono cambiati -> UPDATE
    if (initial && current) {
      const hasChanges = this._hasScheduleChanged(initial, current);
      if (hasChanges) {
        return {
          update: this._mapToUpdateScheduleDto(current),
        };
      }
    }

    // Caso 4: Nessun cambiamento
    return null;
  }

  /**
   * Mappa gli aggiornamenti delle exceptions
   */
  private _mapExceptionsUpdate(
    initial: ResponseUserWeeklyScheduleExceptionDto[] | null,
    current: ResponseUserWeeklyScheduleExceptionDto[] | null,
  ): UserWeeklyScheduleExceptionsUpdateBlockDto | null {
    const initialMap = new Map<
      string,
      ResponseUserWeeklyScheduleExceptionDto
    >();
    const currentMap = new Map<
      string,
      ResponseUserWeeklyScheduleExceptionDto
    >();

    // Crea mappe per facilitare il confronto (usando date come chiave)
    (initial || []).forEach((exception) => {
      if (exception.date) {
        initialMap.set(exception.date, exception);
      }
    });

    (current || []).forEach((exception) => {
      if (exception.date) {
        currentMap.set(exception.date, exception);
      }
    });

    const updateBlock: UserWeeklyScheduleExceptionsUpdateBlockDto = {};

    // Trova elementi da creare (presenti in current ma non in initial)
    const toCreate: CreateUserWeeklyScheduleExceptionDto[] = [];
    currentMap.forEach((currentException, date) => {
      if (!initialMap.has(date)) {
        toCreate.push(this._mapToCreateExceptionDto(currentException));
      }
    });

    // Trova elementi da aggiornare (presenti in entrambi ma modificati)
    const toUpdate: { [key: string]: any } = {};
    currentMap.forEach((currentException, date) => {
      const initialException = initialMap.get(date);
      if (
        initialException &&
        this._hasExceptionChanged(initialException, currentException)
      ) {
        // Usa la data come chiave invece dell'id
        toUpdate[date] = this._mapToUpdateExceptionDto(currentException);
      }
    });

    // Trova elementi da eliminare (presenti in initial ma non in current)
    const toDelete: string[] = [];
    initialMap.forEach((initialException, date) => {
      if (!currentMap.has(date)) {
        // Use date as identifier if createdAt is not available (for locally created exceptions)
        const identifier = initialException.createdAt || date;
        toDelete.push(identifier);
      }
    });

    // Aggiungi solo le sezioni non vuote al DTO
    if (toCreate.length > 0) {
      updateBlock.create = toCreate;
    }

    if (Object.keys(toUpdate).length > 0) {
      updateBlock.update = toUpdate;
    }

    if (toDelete.length > 0) {
      updateBlock.delete = toDelete;
    }

    // Ritorna null se non ci sono cambiamenti
    return Object.keys(updateBlock).length > 0 ? updateBlock : null;
  }

  /**
   * Controlla se il schedule è cambiato
   */
  private _hasScheduleChanged(
    initial: ResponseUserWeeklyScheduleDto,
    current: ResponseUserWeeklyScheduleDto,
  ): boolean {
    return !this._deepEqual(initial.schedule, current.schedule);
  }

  /**
   * Controlla se un'exception è cambiata
   */
  private _hasExceptionChanged(
    initial: ResponseUserWeeklyScheduleExceptionDto,
    current: ResponseUserWeeklyScheduleExceptionDto,
  ): boolean {
    return (
      !this._deepEqual(initial.availability, current.availability) ||
      initial.unvailable !== current.unvailable
    );
  }

  /**
   * Mappa ResponseUserWeeklyScheduleDto a CreateUserWeeklyScheduleDto
   */
  private _mapToCreateScheduleDto(
    schedule: ResponseUserWeeklyScheduleDto,
  ): CreateUserWeeklyScheduleDto {
    const email = this._loggedUserService.currentUser().email;

    return {
      email,
      tenant: environment.tenant,
      schedule: schedule.schedule!,
    };
  }

  /**
   * Mappa ResponseUserWeeklyScheduleDto a UpdateUserWeeklyScheduleDto
   */
  private _mapToUpdateScheduleDto(
    schedule: ResponseUserWeeklyScheduleDto,
  ): UpdateUserWeeklyScheduleDto {
    return {
      schedule: schedule.schedule!,
    };
  }

  /**
   * Mappa ResponseUserWeeklyScheduleExceptionDto a CreateUserWeeklyScheduleExceptionDto
   */
  private _mapToCreateExceptionDto(
    exception: ResponseUserWeeklyScheduleExceptionDto,
  ): CreateUserWeeklyScheduleExceptionDto {
    const email = this._loggedUserService.currentUser().email;

    return {
      date: exception.date!,
      email,
      tenant: environment.tenant,
      availability: exception.availability,
      unvailable: exception.unvailable,
    };
  }

  /**
   * Mappa ResponseUserWeeklyScheduleExceptionDto per l'update
   */
  private _mapToUpdateExceptionDto(
    exception: ResponseUserWeeklyScheduleExceptionDto,
  ): any {
    return {
      availability: exception.availability,
      unvailable: exception.unvailable,
    };
  }

  private _deepEqual(a: any, b: any): boolean {
    if (a === b) return true;

    if (typeof a !== typeof b) return false;

    if (a == null || b == null) return false;

    if (typeof a !== "object") return false;

    if (Array.isArray(a)) {
      if (!Array.isArray(b) || a.length !== b.length) return false;
      return a.every((item, i) => this._deepEqual(item, b[i]));
    }

    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every((key) => this._deepEqual(a[key], b[key]));
  }
}
